import { useNavigate, useParams } from 'react-router-dom'
import React, { useState, useEffect } from 'react';
import { Button, Card, Form, Input, Space, Table, Tooltip } from 'antd';
import { ReloadOutlined, SearchOutlined, DeleteOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import EditEntity from './EditEntity';
import DeleteEntity from './DeleteEntities';
import Config from 'lib/Config';
import { formSchema } from '../../Users/UserConfig';
import { throttle } from 'app/lib/utils/UiUtils';
import { arrayRemove, clone } from 'app/lib/utils/ObjectUtils';

export const createNewEntity = (defaultNewEntity = {}) => {
    return { ...defaultNewEntity, _id: 'new' };
}

type CrudGeneratorPropsTypes = {
    columns?: Array<any>,
    formSchema?: typeof formSchema,
    getEntities?: Function,
    createEntity?: Function,
    editEntity: Function,
    deleteEntities: Function,
    route?: string,
    additionalActionCTAs?: Function,
    CustomComponent?: Function,
    entityName?: any
    modalDetails?: Function,
    newModalDetails?: Function,
    defaultNewEntity?: Object,
    addEditModalFooter: Function | any,
    defaultShowAddEditExpandButton?: Boolean
}

function CrudGenerator({ columns, formSchema, defaultShowAddEditExpandButton, getEntities, defaultNewEntity, createEntity, editEntity, addEditModalFooter, deleteEntities, modalDetails, newModalDetails, route, additionalActionCTAs, CustomComponent,
    entityName: {
        entityNameSingular,
        entityNamePlural,
    } }: CrudGeneratorPropsTypes) {

    // hook that automatically fetches new entities when the filterState changes
    const useFetchEntities = (filterParams) => {
        const [Entities, setEntities] = useState([]);

        const fetchAllEntities = async (params: any) => {
            if (params.date?.length)
                params.date = { start: new Date(params.date?.[0] || Date.now()).toString(), end: new Date(params.date?.[1] || Date.now()).toString() }
            let Entities = await getEntities(params);
            setEntities(Entities);
        }

        useEffect(() => {
            fetchAllEntities(filterParams)
        }, [filterParams]);

        return { Entities, fetchAllEntities, setEntities }
    }

    return function Entity(props) {
        const { searchId } = useParams();
        const navigate = useNavigate();
        const [filterState, setFilterState] = useState({});
        const [filterStateName, setFilterStateName] = useState({});
        const [selectedRows, setSelectedRows] = useState([]);
        const [selectedRowKeys, setSelectedRowKeys] = useState([]);
        const [selectedEntity, setSelectedEntity] = useState(undefined);
        const [deleteEntityIntent, setDeleteEntityIntent] = useState(false);
        const { Entities, fetchAllEntities, setEntities } = useFetchEntities(filterState);

        useEffect(() => {
            setSelectedEntity(searchId == 'new' ? createNewEntity(defaultNewEntity) : Entities.find((entity: any) => entity._id.toString() === searchId));
        }, [Entities, searchId])

        const rowSelection = {
            selectedRowKeys,
            onChange: (selectedRowKeys, selectedRows) => {
                setSelectedRows(selectedRows);
                setSelectedRowKeys(selectedRowKeys)
            },
        };

        const onFinish = (values) => {
            fetchAllEntities({ ...values, ...filterStateName })
        };

        const onReset = () => {
            console.log('fetch all entities on reset')
            setFilterState({});
            setFilterStateName({});
        }

        const onFinishFailed = (errorInfo) => {
            console.log('Failed:', errorInfo);
        };

        const onDoneEditEntity = () => {
            console.log('fetch all entities');
            setSelectedEntity(undefined);
            fetchAllEntities(filterState);
            navigate(`${route}`);
        };

        const onEntitiesDeleted = (r) => {
            console.log(`Entities deleted`, r, filterState)
            setDeleteEntityIntent(false);
            fetchAllEntities(filterState);
            setSelectedRowKeys([]);
            setSelectedRows([]);
        };

        const updateParent = () => {
            console.log('updateParent')
            fetchAllEntities(filterState);
        }

        const hasSearchableColumns = () => {
            return (columns && columns.filter(column => column.shouldUseAsfilter).length > 0);
        }

        const onFilterChange = (name, value) => {
            setFilterStateName({ [name]: value?.dates });
        }

        if (columns.findIndex(column => column.key === 'actions') == -1) {
            columns.push({
                title: 'Actions',
                dataIndex: 'actions',
                key: 'actions',
                render: (_, record) => {
                    return (<Space>
                        <Tooltip title='Edit'>
                            <Button shape="circle" icon={<EditOutlined />} onClick={() => {
                                navigate(`${route}/${record._id}`)
                            }} />
                        </Tooltip>
                    </Space>);
                }
            })
        }

        const content = (

            <div id={`Page-${entityNameSingular}`} className="admin-page">

                <section className="cta-section">

                    {createEntity &&
                        <Button className="cta-button" type="primary" onClick={() => { navigate(`${route}/new`) }}>
                            Create New {entityNameSingular}
                        </Button>
                    }

                    {selectedEntity && <EditEntity
                        Entities={Entities}
                        formSchema={formSchema}
                        entityName={entityNameSingular}
                        editEntity={editEntity}
                        createEntity={createEntity}
                        selectedEntity={selectedEntity}
                        fetchAllEntities={fetchAllEntities}
                        addEditModalFooter={addEditModalFooter}
                        defaultShowAddEditExpandButton={defaultShowAddEditExpandButton}
                        contentClassName="modal-content"
                        modalDetails={modalDetails}
                        newModalDetails={newModalDetails}
                        done={onDoneEditEntity}
                        forceVisibility={true}
                        forceVisibilityCallback={() => { setSelectedEntity(undefined) }}
                        onClose={() => {
                            setSelectedEntity(undefined);
                            navigate(`${route}`)
                        }}
                    />}

                    <Button onClick={() => {
                        fetchAllEntities(filterState)
                    }} className="cta-button" type="default" icon={<ReloadOutlined />}>
                        Reload
                    </Button>

                </section>

                {hasSearchableColumns() ? <section className="search-section cta-section">
                    <Card>
                        <Form
                            name="search"
                            onFinish={onFinish}
                            onReset={onReset}
                            onFinishFailed={onFinishFailed}
                        >
                            {
                                columns.map((column) => {
                                    if (!column.shouldUseAsfilter) return undefined;
                                    const FilterComponent = column.filterComponent || Input;
                                    return (
                                        <Form.Item key={`Search-${column.key}`} className="search-input" name={column.key} >
                                            <FilterComponent placeholder={`Search ${column.title}`} addonBefore={column.title} fetchAllEntities={fetchAllEntities}
                                                {...(column?.name ? ({ onFilterChange: (value) => onFilterChange(column?.name, value) }) : ({}))}
                                            // onChange={e => { // disabled: user should press Enter to commit change
                                            //         setFilterState(fState => {
                                            //             return {
                                            //                 ...fState,
                                            //                 [column.key]: (e.value || e.target) && (e.target.value || e)
                                            //             }
                                            //         })
                                            //     }}
                                            />
                                        </Form.Item>
                                    );
                                })
                            }

                            <div className="search-buttons">
                                <Button htmlType="submit" className="cta-button" type="default" icon={<SearchOutlined />}>Search</Button>
                                <Button htmlType="reset" className="cta-button" type="default" icon={<CloseOutlined />}>Clear Filters</Button>
                            </div>
                        </Form>
                    </Card>
                </section>
                    : null
                }

                <section className="cta-section">
                    <Button disabled={selectedRows.length === 0} onClick={() => { setDeleteEntityIntent(true) }}
                        className="cta-button" type="primary" danger icon={<DeleteOutlined />}>
                        Delete Selected {entityNamePlural}
                    </Button>

                    {deleteEntityIntent ?
                        <DeleteEntity
                            deleteEntities={deleteEntities}
                            onFailure={() => { setDeleteEntityIntent(false) }}
                            onCancel={() => { setDeleteEntityIntent(false) }}
                            onSuccess={() => { onEntitiesDeleted() }}
                            Entities={selectedRows}
                            entityNamePlural={entityNamePlural}
                        />
                        : null
                    }

                    {typeof additionalActionCTAs === "function" &&
                        additionalActionCTAs(selectedRows, setSelectedRowKeys, setSelectedRows, fetchAllEntities)
                    }
                </section>


                { // Render a custom component for this object model listig, if one is defined, or otherwise the default Table:
                    CustomComponent != undefined ?

                        <CustomComponent {...props}
                            Entities={Entities}
                            route={route}
                            columns={columns}
                            filterState={filterState}
                            selectedEntity={selectedEntity}
                            updateParent={updateParent} />
                        :
                        <Table
                            dataSource={Entities}
                            columns={columns}
                            pagination={{ defaultPageSize: Config.ADMIN_TABLE_PAGE_SIZE }}
                            rowKey={(entity: any) => `${entityNameSingular}-${entity._id}`}
                            rowSelection={{
                                type: 'checkbox',
                                ...rowSelection,
                            }}
                            onRow={(record, rowIndex) => {
                                return {
                                    onClick: event => {
                                        let srk = clone(selectedRowKeys);
                                        let eKey = `${entityNameSingular}-${record._id}`;
                                        let eIdx = srk.findIndex(r => r == eKey);
                                        arrayRemove(srk, eKey);
                                        if (eIdx >= 0) srk.splice(eIdx, 1);
                                        else srk = [...selectedRowKeys, eKey];
                                        setSelectedRowKeys(srk);
                                    }
                                };
                            }}
                        />
                }

            </div>
        );

        return content;

    }
}

export default CrudGenerator;
